import React, { useState, useEffect, useCallback } from "react";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import CommandeDetailModal from "../../components/CommandeDetailModal";

function formatDate(str) {
    if (!str) return "";
    const d = new Date(str);
    const yy = String(d.getFullYear()).slice(2);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${yy} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatDateTime(str) {
    if (!str) return null;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const AVATAR_PALETTE = [
    { bg: "#dbeafe", color: "#1d4ed8" },
    { bg: "#dcfce7", color: "#15803d" },
    { bg: "#fef3c7", color: "#b45309" },
    { bg: "#ede9fe", color: "#7c3aed" },
    { bg: "#fce7f3", color: "#be185d" },
    { bg: "#e0f2fe", color: "#0369a1" },
    { bg: "#ffedd5", color: "#c2410c" },
    { bg: "#f0fdf4", color: "#166534" },
];

function hashColor(str) {
    let h = 0;
    for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.codePointAt(i)) & 0xffff;
    return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initials(nom, prenom) {
    return (((nom || "")[0] || "") + ((prenom || "")[0] || "")).toUpperCase() || "?";
}

const CLASSIF_LABELS = {
    PRIMO_MISE_EN_ATTENTE: "Pas de modif / nouvelle ordo",
    RAS_NOUVELLE_ORDO: "Pas de modif / nouvelle ordo",
    PAS_DE_MODIF_NOUVELLE_ORDO: "Pas de modif / nouvelle ordo",
    URGENCE_CHRONIQUE: "Urgence chronique",
    URGENCE_AIGUE: "Urgence aiguë",
    NOUVEL_ENTRANT: "Nouvel entrant",
    DEPART_DC: "Départ · DC",
    DEPART_SIMPLE: "Départ",
    DEPART_HOSPITALISATION: "Départ · Hospitalisation",
    DEPART_VACANCES: "Départ · Départ en vacances",
    CNO_ORTHO_CONTENTION: "CNO, ortho, contention",
    STUP: "STUP",
    INFO_ADMIN: "Info admin",
    LISTE_HP: "Liste HP",
};

const TH = {
    padding: "9px 10px",
    textAlign: "left",
    fontSize: "10px",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 2,
};

const TD = {
    padding: "10px 12px",
    verticalAlign: "middle",
    fontSize: "12.5px",
    color: "#334155",
    borderBottom: "1px solid #f1f5f9",
    whiteSpace: "nowrap",
};

export default function Primo() {
    const { user } = useAuth();
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin]     = useState("");
    const [ehpads, setEhpads]       = useState([]);
    const [ehpadId, setEhpadId]     = useState("");
    const [modalCommandeId, setModalCommandeId] = useState(null);

    useEffect(() => {
        API.get("/ehpads", { noRedirectOn401: true }).then(r => setEhpads(r.data)).catch(() => {});
    }, []);

    const refresh = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (dateDebut) params.append("dateDebut", dateDebut);
        if (dateFin)   params.append("dateFin",   dateFin);
        if (ehpadId)   params.append("ehpadId",   ehpadId);
        const query = params.toString();
        setError(null);
        API.get(query ? `/primo?${query}` : "/primo", { noRedirectOn401: true })
            .then(r => setRows([...r.data].sort((a, b) => {
                if (!!a.miseEnAttente === !!b.miseEnAttente) return 0;
                return a.miseEnAttente ? 1 : -1;
            })))
            .catch(e => {
                console.error(e);
                const status = e.response?.status;
                setError(status === 401
                    ? "Session expirée ou authentification invalide. Veuillez vous reconnecter."
                    : status === 403
                        ? "Accès refusé à l'onglet Primo. Cet accès est réservé aux admin, pharmaciens et superadmin."
                    : "Impossible de charger l'onglet Primo.");
            })
            .finally(() => setLoading(false));
    }, [dateDebut, dateFin, ehpadId]);

    useEffect(() => { refresh(); }, [refresh]);

    const patch = async (id, miseEnAttente) => {
        try {
            const res = await API.patch(`/primo/${id}`, {
                miseEnAttente,
                username: miseEnAttente ? user?.username : null,
            }, { noRedirectOn401: true });
            setRows(prev => prev.map(r => r.id === id ? res.data : r));
        } catch (e) {
            console.error(e);
            setError(e.response?.status === 401
                ? "Accès refusé pour modifier la mise en attente."
                : "Impossible de modifier la mise en attente.");
        }
    };

    const total = rows.length;

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: "white" }}>

            {/* Header */}
            <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                    <span style={{ color: "#8b5cf6", fontSize: "16px", lineHeight: 1 }}>◆</span>
                    <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#0f172a" }}>Primo</h2>
                    <span style={{
                        background: "#f1f5f9", color: "#475569",
                        borderRadius: "999px", fontSize: "12px", fontWeight: 600, padding: "2px 9px",
                    }}>{total}</span>
                    <div style={{ flex: 1 }} />
                    <button onClick={refresh} style={{
                        padding: "6px 14px", borderRadius: "7px",
                        border: "1px solid #e2e8f0", background: "white",
                        cursor: "pointer", fontSize: "12.5px", color: "#475569", fontWeight: 500,
                        display: "flex", alignItems: "center", gap: "6px",
                    }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        Actualiser
                    </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    {[
                        { label: "Du", value: dateDebut, onChange: setDateDebut },
                        { label: "Au", value: dateFin,   onChange: setDateFin },
                    ].map(({ label, value, onChange }) => (
                        <label key={label} style={{ display: "flex", alignItems: "stretch", borderRadius: "7px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                            <span style={{ padding: "5px 10px", fontSize: "12px", fontWeight: 600, color: "#64748b", background: "#f8fafc", borderRight: "1px solid #e2e8f0", display: "flex", alignItems: "center" }}>
                                {label}
                            </span>
                            <input type="date" value={value} onChange={e => onChange(e.target.value)}
                                style={{ padding: "5px 10px", border: "none", fontSize: "12px", color: "#334155", outline: "none", background: "white" }} />
                        </label>
                    ))}
                    <div style={{ display: "flex", alignItems: "stretch", borderRadius: "7px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                        <span style={{ padding: "5px 10px", fontSize: "12px", fontWeight: 600, color: "#64748b", background: "#f8fafc", borderRight: "1px solid #e2e8f0", display: "flex", alignItems: "center" }}>
                            EHPAD
                        </span>
                        <select value={ehpadId} onChange={e => setEhpadId(e.target.value)}
                            style={{ padding: "5px 10px", border: "none", fontSize: "12px", color: "#334155", outline: "none", background: "white", cursor: "pointer" }}>
                            <option value="">Tous</option>
                            {ehpads.map(e => <option key={e.id} value={e.id}>{e.nom || e.code}</option>)}
                        </select>
                    </div>
                </div>
                {error && (
                    <div role="alert" style={{
                        marginTop: "10px",
                        padding: "8px 10px",
                        borderRadius: "7px",
                        border: "1px solid #fecaca",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        fontSize: "12.5px",
                        fontWeight: 600,
                    }}>
                        {error}
                    </div>
                )}
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
                {loading ? (
                    <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>Chargement…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>Aucun patient Pas de modif / nouvelle ordo.</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
                        <thead>
                            <tr>
                                <th style={{ ...TH, minWidth: "220px" }}>Informations</th>
                                <th style={{ ...TH, textAlign: "center" }}>Mise en attente</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <PrimoRow
                                    key={row.id}
                                    row={row}
                                    onToggle={(checked) => patch(row.id, checked)}
                                    onOpenMail={setModalCommandeId}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalCommandeId && (
                <CommandeDetailModal commandeId={modalCommandeId} onClose={() => setModalCommandeId(null)} />
            )}
        </div>
    );
}

function PrimoRow({ row, onToggle, onOpenMail }) {
    const done = !!row.miseEnAttente;
    const { bg: aBg, color: aColor } = hashColor((row.patientNom || "") + (row.patientPrenom || ""));
    const rowBg = done ? "#f5f3ff" : "white";

    return (
        <tr
            style={{ background: rowBg }}
            onMouseEnter={e => { if (!done) e.currentTarget.style.background = "#fafafa"; }}
            onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
        >
            {/* Informations */}
            <td style={{ ...TD, minWidth: "220px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {/* Date + lien mail */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap" }}>
                            {formatDate(row.dateReception)}
                        </span>
                        <button onClick={() => onOpenMail(row.commandeId)} style={{
                            display: "inline-flex", alignItems: "center", gap: "4px",
                            padding: "2px 7px", borderRadius: "5px",
                            border: "1px solid #bfdbfe", background: "#eff6ff",
                            color: "#2563eb", fontSize: "11px", fontWeight: 600, cursor: "pointer",
                        }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="2" y="4" width="20" height="16" rx="2"/>
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </svg>
                            #{row.commandeId}
                        </button>
                        {(row.mailLink || row.gmailLink) && (
                            <a
                                href={row.mailLink || row.gmailLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={row.mailProvider === "ORANGE" ? "Voir dans Orange Mail" : "Voir dans le mail"}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: "3px",
                                    padding: "2px 7px", borderRadius: "5px",
                                    border: "1px solid #fde68a", background: "#fffbeb",
                                    color: "#b45309", fontSize: "11px", fontWeight: 600,
                                    textDecoration: "none", whiteSpace: "nowrap",
                                }}
                            >
                                ✉ Mail
                            </a>
                        )}
                    </div>
                    {/* Nom patient + avatar */}
                    <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                        <div style={{
                            width: "26px", height: "26px", borderRadius: "50%",
                            background: aBg, color: aColor, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "9px", fontWeight: 700,
                        }}>{initials(row.patientNom, row.patientPrenom)}</div>
                        <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "13px" }}>
                            {row.patientPrenom} <span style={{ color: "#475569" }}>{row.patientNom}</span>
                        </span>
                    </div>
                    {/* Classification + EHPAD */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        {row.classification && row.classification !== "UNKNOWN" && (
                            <span style={{
                                display: "inline-block", padding: "2px 8px", borderRadius: "999px",
                                background: "#ede9fe", color: "#7c3aed",
                                fontSize: "11px", fontWeight: 600, alignSelf: "flex-start",
                            }}>
                                {CLASSIF_LABELS[row.classification] || row.classification}
                            </span>
                        )}
                        <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>
                            {row.ehpadNom || row.ehpadCode || "—"}
                            {row.ehpadCode && row.ehpadNom && (
                                <span style={{ color: "#94a3b8", fontSize: "11px", fontWeight: 400 }}> ({row.ehpadCode})</span>
                            )}
                        </span>
                    </div>
                </div>
            </td>

            {/* Mise en attente */}
            <td style={{ ...TD, textAlign: "center" }}>
                <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                    <input
                        type="checkbox"
                        checked={done}
                        onChange={e => onToggle(e.target.checked)}
                        style={{ width: "17px", height: "17px", cursor: "pointer", accentColor: "#8b5cf6" }}
                    />
                    {done && (
                        <div style={{
                            display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "1px",
                            background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: "6px", padding: "3px 6px",
                        }}>
                            {row.miseEnAttenteAt && (
                                <span style={{ fontSize: "10px", color: "#7c3aed", fontWeight: 600, whiteSpace: "nowrap" }}>
                                    {formatDateTime(row.miseEnAttenteAt)}
                                </span>
                            )}
                            {row.miseEnAttenteBy && (
                                <span style={{ fontSize: "10px", color: "#6d28d9", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {row.miseEnAttenteBy}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}
